// Line Entry — the picker's edit queue.
//
// Every edit is recorded PER ROW (a later edit of the same row folds into the
// pending entry and bumps that row's sequence number), and everything edited
// in one debounce window goes to the engine as ONE `recalc_lines` call. A
// reply whose `seq` is older than the row's latest edit is dropped, so a slow
// response never paints over what the user typed after it.

import type { LineRowResult, RecalcRequestRow } from './contract'

export interface RecalcQueueOptions {
  /** Quiet period after the last edit before the batch is sent. */
  delay: number
  send: (rows: RecalcRequestRow[]) => Promise<{ rows: LineRowResult[] }>
  /** One fresh result per row (stale replies never reach this). */
  onResult: (row: LineRowResult) => void
  onError?: (err: unknown) => void
  /** Timer seam for tests. */
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (h: unknown) => void
}

export interface RecalcQueue {
  edit: (srcKey: string, values: Record<string, unknown>, changed: string) => number
  flush: () => Promise<void>
  /** True while a batch is waiting or in flight. */
  pending: () => boolean
  latestSeq: (srcKey: string) => number
  dispose: () => void
}

export function createRecalcQueue(o: RecalcQueueOptions): RecalcQueue {
  const setT = o.setTimer || ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearT = o.clearTimer || ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const seqs = new Map<string, number>()
  const waiting = new Map<string, { values: Record<string, unknown>; changed: Set<string> }>()
  let timer: unknown = null
  let inFlight = 0
  let seqCounter = 0
  let disposed = false

  const flush = async (): Promise<void> => {
    if (timer != null) { clearT(timer); timer = null }
    if (!waiting.size || disposed) return
    const batch: RecalcRequestRow[] = []
    for (const [k, w] of waiting) {
      batch.push({ _src_key: k, values: w.values, changed: Array.from(w.changed), seq: seqs.get(k) || 0 })
    }
    waiting.clear()
    inFlight += 1
    try {
      const res = await o.send(batch)
      if (disposed) return
      for (const r of res?.rows || []) {
        const latest = seqs.get(r._src_key) || 0
        if (typeof r.seq === 'number' && r.seq < latest) continue
        o.onResult(r)
      }
    } catch (err) {
      if (!disposed) o.onError?.(err)
    } finally {
      inFlight -= 1
    }
  }

  return {
    edit(srcKey, values, changed) {
      seqCounter += 1
      seqs.set(srcKey, seqCounter)
      const w = waiting.get(srcKey) || { values: {}, changed: new Set<string>() }
      w.values = { ...values }
      w.changed.add(changed)
      waiting.set(srcKey, w)
      if (timer != null) clearT(timer)
      timer = setT(() => { timer = null; void flush() }, o.delay)
      return seqCounter
    },
    flush,
    pending: () => waiting.size > 0 || inFlight > 0 || timer != null,
    latestSeq: (k) => seqs.get(k) || 0,
    dispose() {
      disposed = true
      if (timer != null) clearT(timer)
      timer = null
      waiting.clear()
    },
  }
}
